import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import apiClient from "@/services/apiClient";
import useLoadData from "@/hooks/useLoadData";
import TwoColumnTable from "@/charts/TwoColumnTable";
import QueryResearchResult from "@/pages/QueryResearch/QueryResearchResult";
import { BACKEND_URL } from "@/config";
import "@/pages/page.css";

function HistoricalQueryGD() {
  const { t, i18n } = useTranslation();
  const DEFAULT_GD_QUERY_LIMIT = 1000;
  const DEFAULT_GD_TIMESTAMP_LIMIT = 100;

  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [GDHistorySearchTerm, setGDHistorySearchTerm] = useState("");

  // Step 1: distinct queries
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [filteredAllGDQueries, setFilteredAllGDQueries] = useState([]);

  const [allGDQueries, allGDQueriesLoaded, allGDQueriesError] = useLoadData(
    `${BACKEND_URL}/api/v1/historical-queries/geo-dashboard/distinct-queries?limit=${DEFAULT_GD_QUERY_LIMIT}`,
  );

  // Step 2: dates available for the selected query
  const [selectedQueryId, setSelectedQueryId] = useState(null);

  const [queryTimestamps, queryTimestampsLoaded, queryTimestampsError] =
    useLoadData(
      selectedQuery
        ? `${BACKEND_URL}/api/v1/historical-queries/geo-dashboard/query-timestamps?query=${encodeURIComponent(
            selectedQuery,
          )}&limit=${DEFAULT_GD_TIMESTAMP_LIMIT}`
        : null,
    );

  // Step 3: full result for the selected date
  const [geoDashboardResult, setGeoDashboardResult] = useState(null);
  const [geoDashboardResultLoading, setGeoDashboardResultLoading] =
    useState(false);
  const [geoDashboardResultError, setGeoDashboardResultError] = useState(null);

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  // Initialize filtered queries when data loads
  useEffect(() => {
    if (allGDQueries) {
      setFilteredAllGDQueries(allGDQueries);
    }
  }, [allGDQueries]);

  // Filter queries based on search term
  useEffect(() => {
    if (allGDQueries && allGDQueries.length > 0) {
      if (GDHistorySearchTerm.trim() === "") {
        setFilteredAllGDQueries(allGDQueries);
      } else {
        const filtered = allGDQueries.filter((query) =>
          query?.toLowerCase().includes(GDHistorySearchTerm.toLowerCase()),
        );
        setFilteredAllGDQueries(filtered);
      }
    }
  }, [GDHistorySearchTerm, allGDQueries]);

  // Reset the selected date whenever a new query is chosen
  useEffect(() => {
    setSelectedQueryId(null);
    setGeoDashboardResult(null);
    setGeoDashboardResultError(null);
  }, [selectedQuery]);

  // Handle selecting a query from the list
  const handleQuerySelect = (query) => {
    setSelectedQuery(query);
    window.scrollTo(0, 0);
  };

  // Handle picking a date from the dropdown
  const handleDateSelect = async (queryId) => {
    setSelectedQueryId(queryId);

    if (!queryId) {
      setGeoDashboardResult(null);
      setGeoDashboardResultError(null);
      return;
    }

    try {
      setGeoDashboardResult(null);
      setGeoDashboardResultLoading(true);
      setGeoDashboardResultError(null);

      const response = await apiClient.get(
        `${BACKEND_URL}/api/v1/historical-queries/geo-dashboard/${queryId}`,
      );

      setGeoDashboardResult(response.data);
    } catch (error) {
      console.error("Error fetching GEO Dashboard result:", error);
      setGeoDashboardResultError(error);
    } finally {
      setGeoDashboardResultLoading(false);
    }
  };

  // Handle back to query list
  const handleBackToList = () => {
    setSelectedQuery(null);
    setSelectedQueryId(null);
    setGeoDashboardResult(null);
    setGeoDashboardResultError(null);
    setGDHistorySearchTerm("");
    window.scrollTo(0, 0);
  };

  // Enhance data with a select action
  const enhancedData = filteredAllGDQueries?.map((query) => ({
    query: <div className="text-truncate">{query}</div>,
    select: (
      <button
        onClick={() => handleQuerySelect(query)}
        className="btn btn-sm btn-outline-primary"
        style={{ border: "none", background: "none", cursor: "pointer" }}
        type="button"
        aria-label={`View results for prompt: ${query}`}
      >
        <ArrowRight size={20} color="var(--brand-color-dark)" />
      </button>
    ),
  }));

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"geo-dashboard-history"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* List View */}
          {!selectedQuery && (
            <>
              <h3 className="h4 fw-bold text-dark">
                {t("historicalGD.title", "GEO Dashboard Processed Prompt Archive")}
              </h3>
              <p className="mt-1 fs-6 text-secondary">
                {t(
                  "historicalGD.subtitle",
                  "This is your GEO Dashboard Processed Prompt Archive Page where you can view and analyze all processed prompts over time.",
                )}
              </p>

                <div className="mb-3 d-flex align-items-center gap-3" data-tour="archived-prompt-gd.search">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t(
                      "historicalGD.searchPlaceholder",
                      "Search Prompts...",
                    )}
                    value={GDHistorySearchTerm}
                    onChange={(e) => setGDHistorySearchTerm(e.target.value)}
                    style={{ maxWidth: 500 }}
                  />
                </div>

                <TwoColumnTable
                  data-tour="archived-prompt-gd.list"
                  data={enhancedData}
                  boxHeight={"300px"}
                  column1Key={"query"}
                  column1Title={t("historicalGD.prompt", "Prompt")}
                  column2Key={"select"}
                  column2Title={t("historicalGD.select", "Select")}
                  isLoaded={allGDQueriesLoaded}
                  error={allGDQueriesError}
                  columnRatio="90-10"
                />
            </>
          )}

          {/* Detail View */}
          {selectedQuery && (
            <>
              <div className="mb-4">
                <button
                  onClick={handleBackToList}
                  className="btn btn-link text-decoration-none"
                  style={{
                    color: "var(--brand-color-dark)",
                    padding: 0,
                    fontWeight: "500",
                  }}
                  type="button"
                >
                  <ArrowLeft size={20} className="me-2" />
                  {t(
                    "historicalGD.backToHistory",
                    "Back to GEO Dashboard Prompt History",
                  )}
                </button>
              </div>

              <div
                className="p-4 bg-white card-radius border mb-4"
                style={{ borderColor: "var(--border)" }}
              >
                <h5 className="fw-bold text-dark mb-3">
                  {t("historicalGD.promptLabel", {
                    query: selectedQuery,
                    defaultValue: `Prompt: ${selectedQuery}`,
                  })}
                </h5>

                <label
                  htmlFor="gd-date-select"
                  className="form-label text-secondary mb-2"
                >
                  {t(
                    "historicalGD.selectDatePrompt",
                    "Select a date to view the result",
                  )}
                </label>
                <select
                  id="gd-date-select"
                  className="form-select"
                  style={{ maxWidth: 400 }}
                  value={selectedQueryId || ""}
                  disabled={!queryTimestampsLoaded}
                  onChange={(e) => handleDateSelect(e.target.value)}
                >
                  <option value="">
                    {queryTimestampsLoaded
                      ? t(
                          "historicalGD.selectDatePlaceholder",
                          "Select a date...",
                        )
                      : t("historicalGD.loadingDates", "Loading dates...")}
                  </option>
                  {queryTimestamps?.map((item) => (
                    <option key={item.query_id} value={item.query_id}>
                      {new Date(item.query_timestamp).toLocaleDateString(
                        i18n.resolvedLanguage || i18n.language,
                      )}
                    </option>
                  ))}
                </select>

                {queryTimestampsError && (
                  <p className="text-danger mt-2 mb-0">
                    {t("historicalGD.errorLoadingDates", {
                      message: queryTimestampsError.message,
                      defaultValue: `Error loading dates: ${queryTimestampsError.message}`,
                    })}
                  </p>
                )}

                {queryTimestampsLoaded && queryTimestamps?.length === 0 && (
                  <p className="text-secondary mt-2 mb-0">
                    {t(
                      "historicalGD.noDatesFound",
                      "No dates found for this prompt.",
                    )}
                  </p>
                )}
              </div>

              {geoDashboardResultLoading && (
                <div className="d-flex flex-column align-items-center mt-5">
                  <div
                    className="spinner-border"
                    role="status"
                    style={{ color: "var(--brand-color-dark)" }}
                  >
                    <span className="visually-hidden">
                      {t("common.loading", "Loading...")}
                    </span>
                  </div>
                  <p className="mt-2 text-secondary">
                    {t(
                      "historicalGD.loadingResult",
                      "Loading GEO Dashboard result...",
                    )}
                  </p>
                </div>
              )}

              {geoDashboardResultError && (
                <p className="text-danger">
                  {t("historicalGD.errorLoadingResult", {
                    message: geoDashboardResultError.message,
                    defaultValue: `Error loading result: ${geoDashboardResultError.message}`,
                  })}
                </p>
              )}

              {geoDashboardResult && (
                <div
                  className="p-4 bg-white card-radius border"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div
                    className="mb-4 pb-3 border-bottom"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h5 className="fw-bold text-dark mb-2">
                          {t("historicalGD.promptLabel", {
                            query: geoDashboardResult.data.query,
                            defaultValue: `Prompt: ${geoDashboardResult.data.query}`,
                          })}
                        </h5>
                        <p className="text-secondary mb-0">
                          {t("historicalGD.queriedOn", {
                            date: new Date(
                              geoDashboardResult.data.query_timestamp,
                            ).toLocaleString(i18n.resolvedLanguage || i18n.language),
                            defaultValue: `Queried on ${new Date(
                              geoDashboardResult.data.query_timestamp,
                            ).toLocaleString(i18n.resolvedLanguage || i18n.language)}`,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  <QueryResearchResult
                    filteredQueryResearchData={geoDashboardResult}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default HistoricalQueryGD;
